import React from 'react';
import { useTranslation } from 'react-i18next';
import U2000ActionBtn from '../../../u2000Core/components/buttons/U2000ActionBtn';
import U2000ResponsiveToolbar from '../../../u2000Core/components/toolbar/U2000ResponsiveToolbar';
import { FaPlus, FaRegFile, FaTrashAlt } from 'react-icons/fa';
import { useU2000AuthContext } from '../../../u2000Core/context/auth/U2000UseAuthContext';
import type { U2000GridStateHandlers } from '../../../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import type { U2000TableHandlers } from '../../../u2000Core/types/U2000TableHandlers';
import { NS, U3208ROLES } from '../../U3208';
import { u3208IsSystemCorpus, type U3208AiRagCorpusSearchRow } from '../../U3208Service';
import { u2000CanManageScopeAwareEntity } from '../../../u2000Core/tools/U2000SystemScopeAccess';

interface U3208AiRagCorpusListToolBarProps {
  gridStateHandlers: U2000GridStateHandlers<U3208AiRagCorpusSearchRow>;
  handlerActions?: U2000TableHandlers<U3208AiRagCorpusSearchRow>;
}

export const U3208AiRagCorpusListToolBar: React.FC<U3208AiRagCorpusListToolBarProps> = ({
  gridStateHandlers,
  handlerActions,
}) => {
  const { t } = useTranslation();
  const { auth, hasRole } = useU2000AuthContext();
  const selectedRows = gridStateHandlers.getSelectedRows();
  const canEditRow = (row: U3208AiRagCorpusSearchRow) =>
    u2000CanManageScopeAwareEntity({
      isSystemScope: u3208IsSystemCorpus(row),
      hasRequiredRole: hasRole(U3208ROLES.EDIT),
      sessionOrganizationId: auth.user.organizationId,
    });
  const canDeleteRow = (row: U3208AiRagCorpusSearchRow) =>
    u2000CanManageScopeAwareEntity({
      isSystemScope: u3208IsSystemCorpus(row),
      hasRequiredRole: hasRole(U3208ROLES.DELETE),
      sessionOrganizationId: auth.user.organizationId,
    });

  const disableUpdateBtn = React.useMemo(
    () => selectedRows.length !== 1 || selectedRows.some((row) => !canEditRow(row)),
    [selectedRows, auth.user.organizationId, hasRole],
  );
  const disableDeleteBtn = React.useMemo(
    () => selectedRows.length === 0 || selectedRows.some((row) => !canDeleteRow(row)),
    [selectedRows, auth.user.organizationId, hasRole],
  );

  const actions = [
    {
      key: 'add',
      label: t('btnAdd', { ns: NS }),
      hasRole: hasRole(U3208ROLES.ADD),
      handleClick: handlerActions?.onAdd,
      icon: FaPlus,
      Component: U2000ActionBtn,
    },
    {
      key: 'modify',
      label: t('btnModify', { ns: NS }),
      hasRole: hasRole(U3208ROLES.EDIT),
      handleClick: handlerActions?.onModify,
      disabled: disableUpdateBtn,
      icon: FaRegFile,
      Component: U2000ActionBtn,
    },
    {
      key: 'delete',
      label: t('btnDelete', { ns: NS }),
      hasRole: hasRole(U3208ROLES.DELETE),
      handleClick: handlerActions?.onDelete,
      disabled: disableDeleteBtn,
      icon: FaTrashAlt,
      Component: U2000ActionBtn,
    },
  ];

  return <U2000ResponsiveToolbar actions={actions} />;
};
